import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCampaignAnalyticsStatsForTimerange } from '../selectors';
import { Segment } from '../types';
import { FiltersContext } from './FiltersContext';
import { useUrlParamsContext } from './UrlParamsContext';

export const FiltersContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const statsForTimeRange = useSelector(selectCampaignAnalyticsStatsForTimerange);
  const {
    exportUrlCAFFOn: FF,
    medium,
    overviewMetricsParam,
    timeSeriesMetricsParam,
    segmentMetricsParam,
    segmentParam,
    regionParam,
    variantIdParam,
  } = useUrlParamsContext();
  const metricsDefault: string[] = [];
  const [overviewMetrics, setOverviewMetrics] = useState(metricsDefault);
  const [timeSeriesMetrics, setTimeSeriesMetrics] = useState(metricsDefault);
  const [segment, setSegment] = useState<Segment>(FF && segmentParam ? segmentParam : undefined);
  const [segmentMetrics, setSegmentMetrics] = useState(metricsDefault);
  const [region, setRegion] = useState(FF && regionParam ? [regionParam] : ['021']);
  const [variantId, setVariantId] = useState<string | number>(
    FF && variantIdParam ? variantIdParam : 'default',
  );
  const [filterString, setFilterString] = useState('');

  /**
   * The campaign's medium is needed to validate any metric selections
   * passed in via the URL, i.e UniqueEmailSend would be a valid
   * selection for an Email campaign, but not for an SMS one.  The campaign's medium
   * is not set until after the first render, so we use an useEffect hook here to watch for
   * when the medium is updated and subsequently set any validated url metric values.
   */
  useEffect(() => {
    if (FF) {
      if (overviewMetricsParam) {
        setOverviewMetrics(overviewMetricsParam);
      }
      if (segmentMetricsParam) {
        setSegmentMetrics(segmentMetricsParam);
      }
    }
  }, [medium]);

  /**
   * Metric validation for the Time Series metrics is additionally dependent on
   * the results returned in statsForTimeRange, so this hook resets the selected
   * time series metrics based on validation that occurs when statsForTimeRange is defined.
   */
  useEffect(() => {
    if (FF && timeSeriesMetricsParam) {
      setTimeSeriesMetrics(timeSeriesMetricsParam);
    }
  }, [JSON.stringify(statsForTimeRange)]);

  return (
    <FiltersContext.Provider
      value={{
        overviewMetrics,
        setOverviewMetrics,
        timeSeriesMetrics,
        setTimeSeriesMetrics,
        segment,
        setSegment,
        segmentMetrics,
        setSegmentMetrics,
        region,
        setRegion,
        variantId,
        setVariantId,
        filterString,
        setFilterString,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};
